
import React, { useState } from 'react';
import { 
  MapPin, 
  Building, 
  Search, 
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  FileSpreadsheet,
  MapPinned,
  CheckCircle,
  XCircle,
  Globe,
  Navigation,
  Home,
  Building2,
  TreePine,
  Copy,
  ArrowUpDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const Coverage = () => {
  const { toast } = useToast();

  // Mock data for coverage areas
  const [coverageAreas] = useState([
    {
      id: 'COV001',
      name: 'Toàn thành phố Hà Nội',
      code: 'HN_ALL',
      hubId: 'HUB001',
      hubName: 'Trung tâm khai thác Hà Nội',
      province: 'Hà Nội',
      district: '',
      ward: '',
      addressType: 'new', // new or old
      status: 'active', // active or inactive
      type: 'Nội thành',
      priority: 1,
      deliveryTime: '1-2 ngày',
      createdAt: '2024-01-15T08:00:00+07:00',
      updatedAt: '2024-01-15T08:00:00+07:00',
      orderCount: 1250,
      isException: false
    },
    {
      id: 'COV002', 
      name: 'Xã An Lạc - Loại trừ',
      code: 'HN_AL_EX',
      hubId: 'HUB001',
      hubName: 'Trung tâm khai thác Hà Nội',
      province: 'Hà Nội',
      district: 'Hà Đông',
      ward: 'An Lạc',
      addressType: 'old',
      status: 'inactive', // Không bao phủ
      type: 'Ngoại thành',
      priority: 2,
      deliveryTime: '',
      createdAt: '2024-01-20T08:00:00+07:00',
      updatedAt: '2024-01-20T08:00:00+07:00',
      orderCount: 0,
      isException: true
    },
    {
      id: 'COV003',
      name: 'Chi nhánh Cầu Giấy - Khu vực',
      code: 'CG_AREA',
      hubId: 'HUB002',
      hubName: 'Chi nhánh Cầu Giấy',
      province: 'Hà Nội',
      district: 'Cầu Giấy',
      ward: '',
      addressType: 'old',
      status: 'active',
      type: 'Nội thành',
      priority: 1,
      deliveryTime: '4-6 giờ',
      createdAt: '2024-02-01T08:00:00+07:00',
      updatedAt: '2024-02-01T08:00:00+07:00',
      orderCount: 320,
      isException: false
    },
    {
      id: 'COV004',
      name: 'Xã An Lạc - Chi nhánh A',
      code: 'CNA_AL',
      hubId: 'HUB005',
      hubName: 'Chi nhánh A',
      province: 'Hà Nội',
      district: 'Hà Đông',
      ward: 'An Lạc',
      addressType: 'old',
      status: 'active',
      type: 'Ngoại thành',
      priority: 3,
      deliveryTime: '3-4 ngày',
      createdAt: '2024-03-01T08:00:00+07:00',
      updatedAt: '2024-03-01T08:00:00+07:00',
      orderCount: 45,
      isException: false
    },
    {
      id: 'COV005',
      name: 'Tuyến TP.HCM - Cần Thơ',
      code: 'HCMCT',
      hubId: 'HUB010',
      hubName: 'Trung tâm miền Nam',
      province: 'Cần Thơ',
      district: '',
      ward: '',
      addressType: 'new',
      status: 'active',
      type: 'Liên tỉnh',
      priority: 1,
      deliveryTime: '2-3 ngày',
      createdAt: '2024-01-25T08:00:00+07:00',
      updatedAt: '2024-01-25T08:00:00+07:00',
      orderCount: 890,
      isException: false
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [addressTypeFilter, setAddressTypeFilter] = useState('all');
  const [hoveredField, setHoveredField] = useState(null);

  // Coverage CRUD states
  const [showCreateCoverageDialog, setShowCreateCoverageDialog] = useState(false);
  const [showEditCoverageDialog, setShowEditCoverageDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [addressTypeCreate, setAddressTypeCreate] = useState('new');
  const [addressTypeEdit, setAddressTypeEdit] = useState('new');

  // Copy function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Đã sao chép",
        description: "Đã sao chép vào clipboard",
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast({
        title: "Lỗi",
        description: "Không thể sao chép",
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Nội thành':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Ngoại thành':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'Liên tỉnh':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getAddressTypeIcon = (type) => {
    return type === 'new' ? <Navigation className="h-3 w-3" /> : <MapPinned className="h-3 w-3" />;
  };

  const getLocationText = (area) => {
    const parts = [area.province, area.district, area.ward].filter(Boolean);
    return parts.length > 0 ? parts.join(' > ') : 'Toàn quốc';
  };

  const handleCreateCoverage = () => {
    setAddressTypeCreate('new');
    setShowCreateCoverageDialog(true);
  };

  const handleViewDetails = (area) => {
    setSelectedCoverage(area);
    setShowDetailsDialog(true);
  };

  const handleEditArea = (area) => {
    setSelectedCoverage(area);
    setAddressTypeEdit(area.addressType);
    setShowEditCoverageDialog(true);
  };

  const handleDeleteArea = (area) => {
    if (confirm(`Bạn có chắc chắn muốn xóa vùng bao phủ "${area.name}"?`)) {
      toast({
        title: "Thành công",
        description: `Đã xóa vùng bao phủ ${area.name}`,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Vùng bao phủ</h1>
          <p className="text-muted-foreground">Quản lý khu vực phục vụ của các hub</p>
        </div>
        <Dialog open={showCreateCoverageDialog} onOpenChange={(open) => {
          setShowCreateCoverageDialog(open);
          if (!open) {
            setAddressTypeCreate('new');
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={handleCreateCoverage}>
              <Plus className="h-4 w-4 mr-2" />
              Thêm vùng bao phủ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Thêm vùng bao phủ mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="coverageName">Tên vùng bao phủ</Label>
                  <Input placeholder="Nhập tên vùng bao phủ" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coverageCode">Mã vùng</Label>
                  <Input placeholder="Nhập mã vùng" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hub">Hub phụ trách</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn hub" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HUB001">Trung tâm khai thác Hà Nội</SelectItem>
                    <SelectItem value="HUB002">Chi nhánh Cầu Giấy</SelectItem>
                    <SelectItem value="HUB003">Bến xe Giáp Bát</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={`grid gap-4 ${addressTypeCreate === 'old' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label htmlFor="province">Tỉnh/Thành phố *</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tỉnh/thành" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hanoi">Hà Nội</SelectItem>
                      <SelectItem value="hcm">TP. Hồ Chí Minh</SelectItem>
                      <SelectItem value="danang">Đà Nẵng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">Quận/Huyện</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn quận/huyện" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tất cả</SelectItem>
                      <SelectItem value="dongda">Đống Đa</SelectItem>
                      <SelectItem value="caugiay">Cầu Giấy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {addressTypeCreate === 'old' && (
                  <div className="space-y-2">
                    <Label htmlFor="ward">Phường/Xã</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn phường/xã" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tất cả</SelectItem>
                        <SelectItem value="langha">Láng Hạ</SelectItem>
                        <SelectItem value="langthuong">Láng Thượng</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Loại vùng</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại vùng" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inner">Nội thành</SelectItem>
                      <SelectItem value="outer">Ngoại thành</SelectItem>
                      <SelectItem value="inter">Liên tỉnh</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Độ ưu tiên</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn độ ưu tiên" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Cao (1)</SelectItem>
                      <SelectItem value="2">Trung bình (2)</SelectItem>
                      <SelectItem value="3">Thấp (3)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="addressType">Loại địa chỉ *</Label>
                  <Select value={addressTypeCreate} onValueChange={setAddressTypeCreate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại địa chỉ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Địa chỉ mới (2 cấp)</SelectItem>
                      <SelectItem value="old">Địa chỉ cũ (3 cấp)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deliveryTime">Thời gian giao hàng</Label>
                  <Input placeholder="VD: 1-2 ngày, 4-6 giờ" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="isException" 
                    className="rounded"
                  />
                  <Label htmlFor="isException">Đây là vùng loại trừ</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setAddressTypeCreate('new');
                  setShowCreateCoverageDialog(false);
                }}>
                  Hủy
                </Button>
                <Button onClick={() => {
                  setAddressTypeCreate('new');
                  setShowCreateCoverageDialog(false);
                  toast({
                    title: "Thành công",
                    description: "Tạo vùng bao phủ mới thành công!",
                  });
                }}>
                  Tạo vùng bao phủ
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng vùng bao phủ</p>
                <p className="text-2xl font-bold">{coverageAreas.length}</p>
              </div>
              <Globe className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Đang hoạt động</p>
                <p className="text-2xl font-bold text-green-600">
                  {coverageAreas.filter(a => a.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Loại trừ</p>
                <p className="text-2xl font-bold text-red-600">
                  {coverageAreas.filter(a => a.isException).length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng đơn hàng</p>
                <p className="text-2xl font-bold">
                  {coverageAreas.reduce((sum, a) => sum + a.orderCount, 0).toLocaleString()}
                </p>
              </div>
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm vùng bao phủ theo tên, mã, địa chỉ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Lọc theo loại" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả loại</SelectItem>
                <SelectItem value="Nội thành">Nội thành</SelectItem>
                <SelectItem value="Ngoại thành">Ngoại thành</SelectItem>
                <SelectItem value="Liên tỉnh">Liên tỉnh</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Lọc theo trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả trạng thái</SelectItem>
                <SelectItem value="active">Hoạt động</SelectItem>
                <SelectItem value="inactive">Không hoạt động</SelectItem>
              </SelectContent>
            </Select>

            <Select value={addressTypeFilter} onValueChange={setAddressTypeFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Loại địa chỉ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả địa chỉ</SelectItem>
                <SelectItem value="new">Địa chỉ mới</SelectItem>
                <SelectItem value="old">Địa chỉ cũ</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Coverage Areas Table */}
      <Card>
        <CardHeader>
          <CardTitle>Danh sách vùng bao phủ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Vùng bao phủ
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Hub phụ trách</TableHead>
                  <TableHead className="min-w-[16em]">Địa điểm</TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Loại
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Ưu tiên</TableHead>
                  <TableHead className="min-w-[16em]">Thời gian giao</TableHead>
                  <TableHead className="min-w-[16em]">Đơn hàng</TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Trạng thái
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="w-[200px] text-center sticky right-0 bg-background z-10 border-l">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coverageAreas.map((area) => (
                  <TableRow key={area.id}>
                    <TableCell>
                      <div>
                        <div 
                          className="font-medium cursor-pointer flex items-center gap-2"
                          onMouseEnter={() => setHoveredField(`name-${area.id}`)}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => copyToClipboard(area.name)}
                        >
                          {area.name}
                          {hoveredField === `name-${area.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          {getAddressTypeIcon(area.addressType)}
                          <span 
                            className="cursor-pointer flex items-center gap-1"
                            onMouseEnter={() => setHoveredField(`code-${area.id}`)}
                            onMouseLeave={() => setHoveredField(null)}
                            onClick={() => copyToClipboard(area.code)}
                          >
                            {area.code}
                            {hoveredField === `code-${area.id}` && (
                              <Copy className="h-3 w-3 text-muted-foreground" />
                            )}
                          </span>
                          {area.isException && (
                            <Badge className="bg-yellow-100 text-yellow-800 text-xs ml-1">
                              Loại trừ
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div 
                        className="flex items-center gap-1 cursor-pointer"
                        onMouseEnter={() => setHoveredField(`hub-${area.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(area.hubName)}
                      >
                        <Building className="h-3 w-3" />
                        <span className="text-sm">{area.hubName}</span>
                        {hoveredField === `hub-${area.id}` && (
                          <Copy className="h-3 w-3 text-muted-foreground ml-1" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div 
                        className="flex items-start gap-1 cursor-pointer"
                        onMouseEnter={() => setHoveredField(`location-${area.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(getLocationText(area))}
                      >
                        <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{getLocationText(area)}</span>
                        {hoveredField === `location-${area.id}` && (
                          <Copy className="h-3 w-3 text-muted-foreground ml-1 mt-0.5" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeColor(area.type)}>
                        {area.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="font-medium">{area.priority}</div>
                        <div className="text-xs text-muted-foreground">
                          {area.priority === 1 ? 'Cao' : area.priority === 2 ? 'TB' : 'Thấp'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{area.deliveryTime || 'Không xác định'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="font-medium">{area.orderCount.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">đơn hàng</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(area.status)}>
                        {area.status === 'active' ? 'Hoạt động' : 'Không hoạt động'}
                      </Badge>
                    </TableCell>
                    <TableCell className="sticky right-0 bg-background z-10 border-l">
                      <div className="flex items-center gap-1 justify-center">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleViewDetails(area)}
                          title="Xem chi tiết"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleEditArea(area)}
                          title="Chỉnh sửa"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDeleteArea(area)}
                          title="Xóa"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Coverage Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Phân tích theo loại vùng</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {['Nội thành', 'Ngoại thành', 'Liên tỉnh'].map((type) => {
                const count = coverageAreas.filter(a => a.type === type).length;
                const percentage = ((count / coverageAreas.length) * 100).toFixed(1);
                return (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={getTypeColor(type)}>{type}</Badge>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{count}</span>
                      <span className="text-muted-foreground"> ({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Hub theo đơn hàng</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
                             {Object.entries(
                 coverageAreas.reduce((acc, area) => {
                   acc[area.hubName] = (acc[area.hubName] || 0) + area.orderCount;
                   return acc;
                 }, {} as Record<string, number>)
               )
                 .sort(([,a], [,b]) => (b as number) - (a as number))
                 .slice(0, 3)
                 .map(([hubName, orderCount], index) => (
                  <div key={hubName} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                      }`}></div>
                      <span className="text-sm">{hubName}</span>
                    </div>
                    <div className="font-medium">{orderCount.toLocaleString()}</div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coverage Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Chi tiết vùng bao phủ - {selectedCoverage?.name}</DialogTitle>
          </DialogHeader>
          {selectedCoverage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-medium">Mã vùng</Label>
                  <p className="text-sm">{selectedCoverage.code}</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Hub phụ trách</Label>
                  <p className="text-sm">{selectedCoverage.hubName}</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Tỉnh/Thành phố</Label>
                  <p className="text-sm">{selectedCoverage.province}</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Quận/Huyện</Label>
                  <p className="text-sm">{selectedCoverage.district || 'Tất cả'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Phường/Xã</Label>
                  <p className="text-sm">{selectedCoverage.ward || 'Tất cả'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Loại địa chỉ</Label>
                  <div className="flex items-center gap-2">
                    {getAddressTypeIcon(selectedCoverage.addressType)}
                    <span className="text-sm">{selectedCoverage.addressType === 'new' ? 'Địa chỉ mới (2 cấp)' : 'Địa chỉ cũ (3 cấp)'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Loại vùng</Label>
                  <Badge className={getTypeColor(selectedCoverage.type)}>
                    {selectedCoverage.type}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Ưu tiên</Label>
                  <p className="text-sm">
                    {selectedCoverage.priority} ({selectedCoverage.priority === 1 ? 'Cao' : selectedCoverage.priority === 2 ? 'Trung bình' : 'Thấp'})
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Thời gian giao hàng</Label>
                  <p className="text-sm">{selectedCoverage.deliveryTime || 'Không xác định'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Số đơn hàng</Label>
                  <p className="text-sm">{selectedCoverage.orderCount.toLocaleString()} đơn</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Trạng thái</Label>
                  <Badge className={getStatusColor(selectedCoverage.status)}>
                    {selectedCoverage.status === 'active' ? 'Hoạt động' : 'Không hoạt động'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Loại trừ</Label>
                  <p className="text-sm">{selectedCoverage.isException ? 'Có' : 'Không'}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-medium">Địa điểm đầy đủ</Label>
                <p className="text-sm">{getLocationText(selectedCoverage)}</p>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                  Đóng
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Coverage Dialog */}
      <Dialog open={showEditCoverageDialog} onOpenChange={(open) => {
        setShowEditCoverageDialog(open);
        if (!open) {
          setAddressTypeEdit('new');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Chỉnh sửa vùng bao phủ - {selectedCoverage?.name}</DialogTitle>
          </DialogHeader>
          {selectedCoverage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editCoverageName">Tên vùng bao phủ *</Label>
                  <Input defaultValue={selectedCoverage.name} placeholder="Nhập tên vùng bao phủ" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editCoverageCode">Mã vùng *</Label>
                  <Input defaultValue={selectedCoverage.code} placeholder="Nhập mã vùng" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editHub">Hub phụ trách *</Label>
                <Select defaultValue={selectedCoverage.hubId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn hub" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HUB001">Trung tâm khai thác Hà Nội</SelectItem>
                    <SelectItem value="HUB002">Chi nhánh Cầu Giấy</SelectItem>
                    <SelectItem value="HUB003">Bến xe Giáp Bát</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={`grid gap-4 ${addressTypeEdit === 'old' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label htmlFor="editProvince">Tỉnh/Thành phố *</Label>
                  <Select defaultValue={selectedCoverage.province.toLowerCase()}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tỉnh/thành" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hanoi">Hà Nội</SelectItem>
                      <SelectItem value="hcm">TP. Hồ Chí Minh</SelectItem>
                      <SelectItem value="danang">Đà Nẵng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editDistrict">Quận/Huyện</Label>
                  <Select defaultValue={selectedCoverage.district || "all"}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn quận/huyện" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tất cả</SelectItem>
                      <SelectItem value="dongda">Đống Đa</SelectItem>
                      <SelectItem value="caugiay">Cầu Giấy</SelectItem>
                      <SelectItem value="hadong">Hà Đông</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {addressTypeEdit === 'old' && (
                  <div className="space-y-2">
                    <Label htmlFor="editWard">Phường/Xã</Label>
                    <Select defaultValue={selectedCoverage.ward || "all"}>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn phường/xã" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tất cả</SelectItem>
                        <SelectItem value="langha">Láng Hạ</SelectItem>
                        <SelectItem value="langthuong">Láng Thượng</SelectItem>
                        <SelectItem value="anlac">An Lạc</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editType">Loại vùng *</Label>
                  <Select defaultValue={selectedCoverage.type}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Nội thành">Nội thành</SelectItem>
                      <SelectItem value="Ngoại thành">Ngoại thành</SelectItem>
                      <SelectItem value="Liên tỉnh">Liên tỉnh</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editPriority">Ưu tiên *</Label>
                  <Select defaultValue={selectedCoverage.priority.toString()}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn ưu tiên" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Cao</SelectItem>
                      <SelectItem value="2">2 - Trung bình</SelectItem>
                      <SelectItem value="3">3 - Thấp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editAddressType">Loại địa chỉ *</Label>
                  <Select value={addressTypeEdit} onValueChange={setAddressTypeEdit}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại địa chỉ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Địa chỉ mới (2 cấp)</SelectItem>
                      <SelectItem value="old">Địa chỉ cũ (3 cấp)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editDeliveryTime">Thời gian giao hàng</Label>
                  <Input defaultValue={selectedCoverage.deliveryTime} placeholder="VD: 1-2 ngày" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="editIsException" 
                    defaultChecked={selectedCoverage.isException}
                    className="rounded"
                  />
                  <Label htmlFor="editIsException">Đây là vùng loại trừ</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setAddressTypeEdit('new');
                  setShowEditCoverageDialog(false);
                }}>
                  Hủy
                </Button>
                <Button onClick={() => {
                  setAddressTypeEdit('new');
                  setShowEditCoverageDialog(false);
                  toast({
                    title: "Thành công",
                    description: "Cập nhật vùng bao phủ thành công!",
                  });
                }}>
                  Cập nhật
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Coverage;
